/**
 * combo-builder-confetti.js
 * Lightweight, dependency-free confetti burst for the "combo added to
 * cart" moment. Single <canvas>, GPU-friendly (transform/opacity only),
 * capped particle count, self-cleans after ~1.4s. No external library —
 * keeps this feature's performance budget self-contained.
 *
 * Respects prefers-reduced-motion: if set, burst() is a no-op — the
 * add-to-cart success state should never rely on this for the actual
 * confirmation (see combo-builder.js, which also updates a status
 * region and the button label regardless of motion preference).
 */
(function (window) {
  'use strict';

  var COLORS = ['#1B3A2B', '#E8632C', '#EFE6D0', '#FFFFFF', '#7C9473'];
  var PARTICLE_COUNT = 46;
  var DURATION_MS = 1400;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLElement} originEl — burst originates from this element's center (typically the Add to Cart button)
   */
  function burst(originEl) {
    if (prefersReducedMotion()) return;
    if (!originEl) return;

    var rect = originEl.getBoundingClientRect();
    var originX = rect.left + rect.width / 2;
    var originY = rect.top + rect.height / 2;

    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'fixed';
    canvas.style.inset = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    var dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    var particles = [];
    for (var i = 0; i < PARTICLE_COUNT; i++) {
      var angle = (Math.random() * Math.PI * 0.9) + Math.PI * 1.05; // mostly upward cone
      var speed = 4 + Math.random() * 7;
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 5 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 12,
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
        life: 1
      });
    }

    var start = null;
    var gravity = 0.22;

    function frame(ts) {
      if (!start) start = ts;
      var elapsed = ts - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(function (p) {
        p.vy += gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.life = Math.max(0, 1 - elapsed / DURATION_MS);

        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });

      if (elapsed < DURATION_MS) {
        window.requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }

    window.requestAnimationFrame(frame);
  }

  window.ComboBuilderConfetti = { burst: burst };
})(window);
